import { useCallback, useMemo, useState } from 'react';
import { useTransactionAdder } from '../state/transactions/hooks';
import { useDoUpdate, useOnchainInteractionsLendingPool, useOnchainLendingPool } from './useOnchain';
import { ButtonState } from '../components/InteractionButton';
import { formatFloat } from '../utils/format';
import useGenericCall from './useGenericCall';
import { useOffchainLendingPool } from './useOffchain';


export default function useReinvest(): [ButtonState, () => Promise<void>] {
  const onchainInteractionsLendingPool = useOnchainInteractionsLendingPool();
  const doUpdate = useDoUpdate();
  const addTransaction = useTransactionAdder();
  const [pending, setPending] = useState<boolean>(false);

  const data = useGenericCall(useOffchainLendingPool(), async offchainLendingPool => ({
    symbolA: await offchainLendingPool.getBorrowableA().getSymbol(),
    symbolB: await offchainLendingPool.getBorrowableB().getSymbol(),
  }));
  const pendingReward = useGenericCall(useOnchainLendingPool(), r => r.getPendingReward());
  const summary = data && pendingReward ? `Reinvest  ${formatFloat(pendingReward.amount, 3)} ${pendingReward.symbol} on ${data.symbolA}-${data.symbolB}` : '';
  
  const state: ButtonState = useMemo(() => {
    if (pending) return ButtonState.Pending;
    return ButtonState.Ready;
  }, [pending]);

  const action = useCallback(async (): Promise<void> => {
    if (state !== ButtonState.Ready) return;
    setPending(true);
    try {
      await onchainInteractionsLendingPool.reinvest((hash: string) => {
        addTransaction({ hash }, { summary });
      });
      doUpdate();
    }
    finally {
      setPending(false);
    }
  }, [state, onchainInteractionsLendingPool, addTransaction, summary]);

  return [state, action];
}